/* Backing-track arrangement for songs: strummed chords, bass, drums and (for original songs)
   a generated practice melody. */

import type { PreparedSong } from '../../content/songs';
import { type AudioEvent, audio } from '../../core/audio';
import { rng as seededRng } from '../../core/dsp/rng';
import { chordPcs, mod, type ParsedChord, parseChord, scaleNotes } from '../../core/theory';
import { choice, clamp, hashStr } from '../../core/util';

interface MelodyNote {
  bar: number;
  beat: number;
  dur: number;
  midi: number;
}

const melodyCache = new Map<string, MelodyNote[]>();

/** Deterministic practice melody over the chords (original songs only; covers never get one). */
export function melodyFor(song: PreparedSong): MelodyNote[] {
  const cached = melodyCache.get(song.id);
  if (cached) return cached;
  const rng = seededRng(hashStr(`${song.id}-mel`));
  const scale = scaleNotes(song.tonic, song.mode === 'minor' ? 'minor' : 'major', 62, 81);
  const rh4: [number, number][][] = [
    [
      [0, 1],
      [1, 1],
      [2, 2],
    ],
    [
      [0, 2],
      [2, 1],
      [3, 1],
    ],
    [
      [0, 1],
      [1, 1],
      [2, 1],
      [3, 1],
    ],
    [
      [0, 3],
      [3, 1],
    ],
    [
      [0, 1.5],
      [1.5, 0.5],
      [2, 2],
    ],
    [
      [1, 1],
      [2, 1],
      [3, 1],
    ],
  ];
  const rh3: [number, number][][] = [
    [
      [0, 2],
      [2, 1],
    ],
    [
      [0, 1],
      [1, 1],
      [2, 1],
    ],
    [[0, 3]],
  ];
  const out: MelodyNote[] = [];
  let prev = scale[Math.floor(scale.length / 2)] as number;
  const tones = scale.filter((m, i, a) => a.indexOf(m) === i);
  const nearest = (list: number[], to: number) =>
    list.reduce((best, x) => (Math.abs(x - to) < Math.abs(best - to) ? x : best), list[0] as number);
  song.bars.forEach((bar, bi) => {
    const c = parseChord(bar.chord) as ParsedChord;
    const pcs = chordPcs(c.root, c.q);
    const ctones: number[] = [];
    for (let m = 62; m <= 81; m++) if (pcs.includes(mod(m, 12))) ctones.push(m);
    const sec = song.sections[bar.sec] as PreparedSong['sections'][number];
    const idxInSec = song.bars.filter((b, k) => k < bi && b.sec === bar.sec).length;
    const lastOfPhrase = (idxInSec + 1) % 4 === 0 || idxInSec === sec.bars.length - 1;
    const rhythms = song.beats === 3 ? rh3 : rh4;
    const rh: [number, number][] = lastOfPhrase
      ? song.beats === 3
        ? [[0, 3]]
        : [
            [0, 1],
            [1, 3],
          ]
      : choice(rhythms, rng);
    rh.forEach(([b, d], k) => {
      let m: number;
      if (k === 0 || (lastOfPhrase && k === rh.length - 1)) {
        const pool = ctones.slice().sort((a, b2) => Math.abs(a - prev) - Math.abs(b2 - prev));
        m = pool[Math.min(pool.length - 1, Math.floor(rng() * 2))] as number;
        if (lastOfPhrase && k === rh.length - 1) {
          const roots = ctones.filter((x) => mod(x, 12) === c.root);
          if (roots.length) m = roots.sort((a, b2) => Math.abs(a - prev) - Math.abs(b2 - prev))[0] as number;
        }
      } else {
        const i = tones.indexOf(nearest(tones, prev));
        const dir = rng() < 0.5 ? -1 : 1;
        m = tones[clamp(i + dir * (rng() < 0.75 ? 1 : 2), 0, tones.length - 1)] as number;
        if (!pcs.includes(mod(m, 12)) && d >= 1) m = nearest(ctones, m);
      }
      out.push({ bar: bi, beat: b, dur: d, midi: m });
      prev = m;
    });
  });
  melodyCache.set(song.id, out);
  return out;
}

export interface ArrangeOptions {
  from?: number;
  to?: number;
  /** tempo multiplier */
  tempo?: number;
  bass?: boolean;
  drums?: boolean;
  chords?: boolean;
  melody?: boolean;
  noGate?: boolean;
  chordVel?: number;
}

export function arrange(song: PreparedSong, o: ArrangeOptions = {}): { ev: AudioEvent[]; length: number } {
  const from = o.from || 0;
  const to = o.to == null ? song.bars.length : o.to;
  const bpm = song.bpm * (o.tempo || 1);
  const beat = 60 / bpm;
  const bars = song.bars.slice(from, to);
  const prog = audio.progEvents(
    bars.map((b) => b.chord),
    {
      bpm,
      beats: song.beats,
      pattern: song.pattern,
      bass: o.bass,
      drums: o.drums,
      noChords: o.chords === false,
      noGate: o.noGate,
      chordVel: o.chordVel || 1,
    },
  );
  const ev: AudioEvent[] = prog.ev.map((e) =>
    e.cue?.type === 'bar' ? { ...e, cue: { ...e.cue, index: e.cue.index + from } } : e,
  );
  if (o.melody && !song.cover) {
    for (const n of melodyFor(song)) {
      if (n.bar < from || n.bar >= to) continue;
      ev.push({
        kind: 'note',
        inst: 'piano',
        midi: n.midi,
        t: ((n.bar - from) * song.beats + n.beat) * beat,
        dur: n.dur * beat * 0.95,
        vel: 0.42,
        noGate: o.noGate,
      });
    }
  }
  return { ev, length: prog.length };
}
